import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  FieldType,
  FieldTypeConfig,
  FormlyConfig,
  FormlyFieldConfig,
  FormlyFormOptions,
} from '@ngx-formly/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TableColumn } from '../../../models/irembo-data-table.model';
import {
  TableWithInputColumn,
  TableWithInputColumnKind,
  TableWithInputColumnOption,
  TableWithInputRow,
  TableWithInputUniqueFieldsCombination,
  getTableWithInputOptionValue,
  isTableWithInputColumnTypeAllowed,
  resolveTableWithInputColumnKind,
  resolveTableWithInputSelectDisplayValues,
} from '../../../models/irembo-table-with-input.model';
import { ToastService } from '../../../services/toast/toast.service';
import { evaluateSafeExpression } from './custom-table-with-input-expression.util';

interface UniquenessError {
  key: string;
  message: string;
}

// `values` is resolved display text (never itself translatable -- it's the
// row's actual data), kept apart from `message` (a translation key/phrase)
// so the template can translate `message` on its own instead of trying to
// translate the two joined together, which could never match a static
// translation table entry.
interface CombinationUniquenessError {
  values: string;
  message: string;
  isCustomMessage: boolean;
}

// One <formly-form> per column in the add/edit panel, all bound to the same
// rowForm/rowModel: the panel renders its own label above and messages below
// each field, which a single formly-form spanning every column couldn't
// interleave. Each instance needs its own options object — formly keeps
// per-form state on it.
interface RowFieldSet {
  column: TableWithInputColumn;
  fields: FormlyFieldConfig[];
  options: FormlyFormOptions;
}

// What the panel says for a validation error the column itself gives no
// wording for. Keyed by the control's error name (Angular's standard
// validators, which formly adds from a field's props), as fixed phrases so
// each stays a translatable key. A column overrides any of these — or names
// an error not listed here — through its `validation.messages`.
const DEFAULT_COLUMN_ERROR_MESSAGES: Record<string, string> = {
  required: 'This field is required',
  email: 'Please enter a valid email address',
  minlength: 'This value is too short',
  maxlength: 'This value is too long',
  min: 'This value is too small',
  max: 'This value is too large',
  pattern: 'This value is not in the expected format',
};
const GENERIC_COLUMN_ERROR_MESSAGE = 'This value is not valid';

let rowIdCounter = 0;
const generateRowId = (): string =>
  `${Date.now().toString(36)}-${(rowIdCounter++).toString(36)}`;

const minItemsValidator = (getMinItems: () => number): ValidatorFn => {
  return control => {
    const minItems = getMinItems();
    if (!minItems) {
      return null;
    }
    const value = control.value;
    const length = Array.isArray(value) ? value.length : 0;
    return length < minItems ? { minItems: true } : null;
  };
};

@Component({
  standalone: false,
  selector: 'irembogov-custom-table-with-input',
  templateUrl: './custom-table-with-input.component.html',
  styleUrls: ['./custom-table-with-input.component.scss'],
})
export class CustomTableWithInputComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  @ViewChild('confirmModalTpl') confirmModalTpl!: TemplateRef<unknown>;

  rows: TableWithInputRow[] = [];
  selectedRows: TableWithInputRow[] = [];
  searchTerm = '';

  panelOpen = false;
  panelMode: 'add' | 'edit' = 'add';
  rowForm!: FormGroup;
  // Seeded with the same values as rowForm's controls (see buildRowForm), so
  // formly finds nothing to reconcile between the two when it binds.
  rowModel: Record<string, unknown> = {};
  rowFieldSets: RowFieldSet[] = [];
  editingRowId: string | null = null;
  uniquenessErrors: UniquenessError[] = [];
  // Composite uniqueness (see uniqueFieldsCombination) is a single condition
  // spanning two fields, not a per-field one -- surfaced as one message above
  // the form instead of duplicating it under each of the two fields.
  uniqueFieldsCombinationError: CombinationUniquenessError | null = null;

  confirmTitle = '';
  confirmMessage = '';
  // Set alongside confirmMessage when a specific value (e.g. the row being
  // deleted) should render bold within the dialog copy instead of plain text.
  confirmMessagePrefix = '';
  confirmMessageValue: string | null = null;
  confirmMessageSuffix = '';
  confirmAcceptLabel = 'Confirm';
  confirmCancelLabel = 'Cancel';
  confirmVariant: 'danger' | 'warning' = 'danger';
  private confirmAction: (() => void) | null = null;

  // filteredRows is read every change-detection cycle via the [data]/
  // [collectionSize] template bindings (this component isn't OnPush). Without
  // caching, a truthy searchTerm makes it re-run .filter() and return a brand
  // new array on every single cycle — even when neither rows nor searchTerm
  // actually changed — which the child table treats as new data and resets
  // its current page for, silently kicking users back to page 1 while
  // searching. Cache by (rows reference, searchTerm) so it only recomputes
  // when one of those genuinely changes.
  private filteredRowsCache: TableWithInputRow[] | null = null;
  private filteredRowsCacheSource: TableWithInputRow[] | null = null;
  private filteredRowsCacheTerm = '';

  // commitRows() flips this on for the duration of its own setValue() call,
  // so the subscription below can tell that write apart from one arriving
  // from outside this component — e.g. draft-resume or RFA data arriving
  // asynchronously and getting patched into the form after this field
  // already initialized with an empty/stale value. commitRows() must still
  // let setValue() emit (formly's own FieldType subscribes to
  // control.valueChanges to keep the plain `model` object it hands back on
  // submit in sync — emitEvent:false would suppress that too, and every
  // consumer that submits `model` directly, not `form.value`, would send a
  // stale/default value for this field no matter what the user entered).
  private isCommittingRows = false;
  private valueChangesSubscription: Subscription | null = null;
  // Watches the currently open add/edit row form for column-level
  // `expressions`/`dependentOptions` (see wireRowFormReactivity) — replaced
  // whenever a new rowForm is built, and torn down when no panel is open.
  private rowFormExpressionsSubscription: Subscription | null = null;
  // Watches the currently open add/edit row form to re-run uniqueness checks
  // (see wireRowFormUniquenessChecking) the moment a duplicate is entered,
  // rather than only discovering it when Save is clicked. Wired
  // unconditionally (unlike rowFormExpressionsSubscription above, which only
  // exists when the table actually uses expressions/dependentOptions),
  // since per-column `unique` and uniqueFieldsCombination are far more
  // commonly configured.
  private rowFormUniquenessSubscription: Subscription | null = null;
  // Guards against the patchValue() inside applyRowFormReactivity()
  // re-triggering the very valueChanges subscription that called it.
  private isApplyingRowExpressions = false;
  // Dropdown options for columns with `dependentOptions`, keyed by column
  // key — meaningful only for the currently open add/edit row form, recomputed
  // on every change to that form. A column with no `dependentOptions` (or
  // one whose dependsOn value has no match yet) is simply absent here; the
  // template falls back to that column's own static props.options.
  dependentColumnOptions: Record<string, TableWithInputColumnOption[]> = {};
  // Keys of the columns whose `expressions.hide` is currently truthy for the
  // open add/edit row form (see wireRowFormReactivity). Their fields are left
  // out of the panel, and their controls are disabled and cleared.
  private hiddenColumnKeys = new Set<string>();
  // Current required state of the columns that carry an
  // `expressions['props.required']`, keyed by column key. Such a column's
  // static `props.required` is only its starting point; once evaluated this
  // is what applies (see isColumnRequired). Other columns are absent.
  private requiredOverrides: Record<string, boolean> = {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly formlyConfig: FormlyConfig
  ) {
    super();
  }

  ngOnInit(): void {
    this.syncRowsFromControlValue(this.formControl.value);

    // The form's model can be populated after this field has already
    // initialized — e.g. saved draft/RFA data fetched asynchronously and
    // patched into the reactive form once it arrives. Without this, `rows`
    // (and therefore the rendered table) stays frozen at whatever value was
    // present at construction time, typically empty.
    this.valueChangesSubscription = this.formControl.valueChanges.subscribe(
      value => {
        if (this.isCommittingRows) {
          return;
        }
        this.syncRowsFromControlValue(value);
      }
    );

    this.formControl.addValidators(minItemsValidator(() => this.minItems));
    if (this.minItems) {
      this.field.validation ??= {};
      this.field.validation.messages = {
        ...this.field.validation.messages,
        minItems:
          this.field.validation?.messages?.['minItems'] ||
          `${this.translate.instant('Minimum of')} ${this.minItems} ${
            this.minItems === 1
              ? this.translate.instant('item required.')
              : this.translate.instant('items required.')
          }`,
      };
    }
    this.formControl.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
    this.rowFormExpressionsSubscription?.unsubscribe();
    this.rowFormUniquenessSubscription?.unsubscribe();
  }

  // Persisted/preloaded values are plain application data and may not carry
  // our internal __rowId — backfill it here so every row has a stable id to
  // key edit/delete/selection off, and never let it leak back out (see
  // commitRows).
  private syncRowsFromControlValue(value: unknown): void {
    const incomingRows: Record<string, unknown>[] = Array.isArray(value)
      ? value
      : [];
    this.rows = incomingRows.map(row =>
      row['__rowId']
        ? (row as TableWithInputRow)
        : ({ ...row, __rowId: generateRowId() } as TableWithInputRow)
    );
  }

  // A table cannot be nested inside a table: a column configured with this
  // component's own type is dropped (and reported once per configuration)
  // rather than rendered. Memoized on the configured array's identity —
  // this getter is read on every change-detection cycle.
  private columnsCacheSource: TableWithInputColumn[] | null = null;
  private columnsCache: TableWithInputColumn[] = [];

  get columns(): TableWithInputColumn[] {
    const configured: TableWithInputColumn[] =
      this.field.props?.['columns'] || [];
    if (configured !== this.columnsCacheSource) {
      const rejected = configured.filter(
        column => !isTableWithInputColumnTypeAllowed(column.type)
      );
      if (rejected.length) {
        console.error(
          `Table with input "${String(
            this.field.key
          )}" ignores column(s) ${rejected
            .map(column => column.key)
            .join(', ')}: a table cannot be nested inside a table.`
        );
      }
      this.columnsCache = rejected.length
        ? configured.filter(column =>
            isTableWithInputColumnTypeAllowed(column.type)
          )
        : configured;
      this.columnsCacheSource = configured;
    }
    return this.columnsCache;
  }

  get tableColumns(): TableColumn[] {
    return this.columns
      .filter(column => !column.props?.hiddenColumn)
      .map(column => ({
        key: column.key,
        title: column.props?.label ?? column.key,
        width: column.props?.widthPercent
          ? `${column.props.widthPercent}%`
          : undefined,
      }));
  }

  // My row template always adds one more <td> than `tableColumns` for the
  // edit/delete action icons, but the shared table's own header row is built
  // from whatever is bound to [columns] — with no matching blank <th> for
  // that extra column, the header row (and its background) falls short of
  // the actions column's width. Bound to [columns] instead of tableColumns
  // (header/column-count purposes only — my row template still iterates
  // tableColumns directly for its data cells, so this doesn't add a stray
  // data cell).
  //
  // Memoized: this is bound straight to the shared table's [columns] input,
  // which is read on every change-detection cycle (this component isn't
  // OnPush). Building a new array on every call — regardless of whether
  // anything actually changed — made the child table treat [columns] as
  // "new data" on every unrelated CD tick anywhere in the app, silently
  // resetting its current page back to 1. Cache by the inputs that actually
  // determine the result.
  private headerColumnsCache: TableColumn[] | null = null;
  private headerColumnsCacheKey: string | null = null;

  get headerColumns(): TableColumn[] {
    const key = JSON.stringify([
      this.columns,
      this.isReadOnly,
      this.enableEdit,
      this.enableDeletion,
    ]);
    if (this.headerColumnsCache && this.headerColumnsCacheKey === key) {
      return this.headerColumnsCache;
    }
    this.headerColumnsCache =
      this.isReadOnly || (!this.enableEdit && !this.enableDeletion)
        ? this.tableColumns
        : [
            ...this.tableColumns,
            // A soft target, not a hard floor — the table must always fit
            // its container without scrolling, so this column has to be
            // able to shrink like any other. The edit/delete buttons wrap
            // onto their own line when there isn't room side by side (see
            // the actions <td> in the template, which isn't nowrap) instead
            // of a fixed minimum width forcing the whole table wider than
            // its container.
            { key: '__actions', title: '', width: '25%' },
          ];
    this.headerColumnsCacheKey = key;
    return this.headerColumnsCache;
  }

  // Application review/preview pages (UC-OFF-07/UC-CIT-07) render this field
  // with its control disabled — in that context every interactive affordance
  // (search, row selection, Add Item, edit/delete icons) should disappear
  // entirely rather than just render disabled, leaving a plain read-only
  // table + item count.
  get isReadOnly(): boolean {
    return this.formControl.disabled;
  }

  get filteredRows(): TableWithInputRow[] {
    if (!this.showSearch || !this.searchTerm.trim()) {
      return this.rows;
    }
    if (
      this.filteredRowsCache &&
      this.filteredRowsCacheSource === this.rows &&
      this.filteredRowsCacheTerm === this.searchTerm
    ) {
      return this.filteredRowsCache;
    }
    const term = this.searchTerm.trim().toLowerCase();
    // Search against the same formatted/translated value the table
    // displays (e.g. a resolved dropdown label, Yes/No for a checkbox) so
    // matches line up with what the user can actually see, not the raw
    // stored value underneath it.
    this.filteredRowsCache = this.rows.filter(row =>
      this.tableColumns.some(column =>
        this.formatCellValue(row, column).toLowerCase().includes(term)
      )
    );
    this.filteredRowsCacheSource = this.rows;
    this.filteredRowsCacheTerm = this.searchTerm;
    return this.filteredRowsCache;
  }

  get enableEdit(): boolean {
    return this.field.props?.['enableEdit'] ?? true;
  }

  get enableDeletion(): boolean {
    return this.field.props?.['enableDeletion'] ?? true;
  }

  get enableRowSelection(): boolean {
    return this.field.props?.['enableRowSelection'] ?? false;
  }

  get showSearch(): boolean {
    return this.field.props?.['showSearch'] ?? false;
  }

  get showPagination(): boolean {
    return this.field.props?.['showPagination'] ?? true;
  }

  get itemsPerPage(): number {
    return this.field.props?.['itemsPerPage'] ?? 10;
  }

  get maxItems(): number | null {
    return this.field.props?.['maxItems'] ?? null;
  }

  get uniqueFieldsCombination():
    | TableWithInputUniqueFieldsCombination
    | undefined {
    return this.field.props?.['uniqueFieldsCombination'];
  }

  // Only fall back to a minimum of 1 when the SM has explicitly marked the
  // whole table required — an untouched/optional table must stay optional
  // rather than silently requiring at least one row.
  get minItems(): number {
    const configured = this.field.props?.['minItems'];
    if (configured !== undefined && configured !== null) {
      return configured;
    }
    return this.field.props?.required ? 1 : 0;
  }

  get isMaxItemsReached(): boolean {
    return !!this.maxItems && this.rows.length >= this.maxItems;
  }

  columnKind(column: TableWithInputColumn): TableWithInputColumnKind {
    return resolveTableWithInputColumnKind(column);
  }

  // The add/edit panel's dropdown field is built from here rather than
  // column.props.options directly, so a column with `dependentOptions`
  // shows only the subset matching its dependsOn field's current value.
  // A column without `dependentOptions` (the common case) is never in
  // dependentColumnOptions, so this is just column.props.options — no
  // behavior change for existing dropdown columns.
  getColumnOptions(column: TableWithInputColumn): TableWithInputColumnOption[] {
    return (
      this.dependentColumnOptions[column.key] ?? column.props?.options ?? []
    );
  }

  formatCellValue(row: TableWithInputRow, column: TableColumn): string {
    const value = (row as Record<string, unknown>)[column.key];
    const columnConfig = this.columns.find(c => c.key === column.key);
    if (columnConfig) {
      const kind = resolveTableWithInputColumnKind(columnConfig);
      if (kind === 'checkbox') {
        return this.translate.instant(value ? 'Yes' : 'No');
      }
      if (kind === 'select') {
        // An empty value (a cleared or never-filled dropdown) resolves to ''
        // — translate.instant throws on an empty key, which would abort the
        // whole row's rendering, so only non-empty labels are translated.
        return resolveTableWithInputSelectDisplayValues(columnConfig, value)
          .filter(label => label !== '')
          .map(label => this.translate.instant(label))
          .join(', ');
      }
    }
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  // Every validation error currently on a column's control, as the text to
  // show under its field — one entry per error, in the order the control
  // reports them. Which errors can occur is up to the column's type and
  // props (required, pattern, minLength, a type's own validators…); the
  // wording for each comes from resolveColumnErrorMessage, so a new error
  // needs no change here or in the template.
  columnErrors(column: TableWithInputColumn): string[] {
    const control = this.rowForm?.get(column.key);
    if (!control?.touched || !control.errors) {
      return [];
    }
    return Object.entries(control.errors).map(([name, error]) =>
      this.resolveColumnErrorMessage(column, name, error)
    );
  }

  // Wording for one validation error, most specific source first:
  //   1. the column's own `validation.messages[<error>]` — what the
  //      configuration wizard writes;
  //   2. this panel's defaults for the standard validators;
  //   3. the message registered with formly for that validator name (e.g.
  //      `invalidfileformat`); a function message gets the field built for
  //      this column, as it would standalone;
  //   4. a generic phrase, so an unknown error is never shown as nothing.
  private resolveColumnErrorMessage(
    column: TableWithInputColumn,
    name: string,
    error: unknown
  ): string {
    const configured = column.validation?.messages?.[name];
    if (configured) {
      return configured;
    }
    if (DEFAULT_COLUMN_ERROR_MESSAGES[name]) {
      return DEFAULT_COLUMN_ERROR_MESSAGES[name];
    }
    const registered = this.formlyConfig.getValidatorMessage(name);
    if (typeof registered === 'string') {
      return registered;
    }
    if (typeof registered === 'function') {
      const field = this.rowFieldSets.find(set => set.column.key === column.key)
        ?.fields[0] ?? { key: column.key };
      const message = registered(error, field);
      if (typeof message === 'string') {
        return message;
      }
    }
    return GENERIC_COLUMN_ERROR_MESSAGE;
  }

  // The panel renders only the columns not currently hidden by their
  // `expressions.hide`; a hidden column's formly-form is torn down, and
  // rebuilt against the same (kept) control when it is shown again.
  get visibleRowFieldSets(): RowFieldSet[] {
    return this.rowFieldSets.filter(
      fieldSet => !this.hiddenColumnKeys.has(fieldSet.column.key)
    );
  }

  // Whether the panel treats this column as required right now: the result
  // of its `expressions['props.required']` when it has one, its static
  // `props.required` otherwise.
  isColumnRequired(column: TableWithInputColumn): boolean {
    return column.key in this.requiredOverrides
      ? this.requiredOverrides[column.key]
      : !!column.props?.required;
  }

  // What an untouched/cleared control of this column holds.
  private defaultValueFor(column: TableWithInputColumn): unknown {
    const kind = resolveTableWithInputColumnKind(column);
    if (kind === 'checkbox') {
      return column.props?.default ?? false;
    }
    if (kind === 'select' && column.props?.multiple) {
      return [];
    }
    return null;
  }

  // The controls are built here, ahead of formly: formly reuses a control
  // that already exists on the form it is handed, so every field type
  // renders against these (and their defaults), and the row form is usable
  // — setValue, validity — before the panel has even been rendered.
  private buildRowForm(initial?: TableWithInputRow): FormGroup {
    const group: Record<string, unknown> = {};
    const model: Record<string, unknown> = {};
    this.columns.forEach(column => {
      const validators = [];
      if (column.props?.required) {
        validators.push(Validators.required);
      }
      if (resolveTableWithInputColumnKind(column) === 'email') {
        validators.push(Validators.email);
      }
      const defaultValue = this.defaultValueFor(column);
      const initialValue = initial
        ? (initial as Record<string, unknown>)[column.key] ?? defaultValue
        : defaultValue;
      group[column.key] = [
        { value: initialValue, disabled: !!column.props?.disabled },
        validators,
      ];
      model[column.key] = initialValue;
    });
    const form = this.fb.group(group);
    this.rowModel = model;
    this.rowFieldSets = this.columns.map(column => ({
      column,
      fields: [this.buildRowField(column)],
      options: {},
    }));
    this.wireRowFormReactivity(form);
    this.wireRowFormUniquenessChecking(form);
    return form;
  }

  // What formly is handed for a column is deliberately narrower than the
  // column config: the row's `expressions` and `dependentOptions` stay with
  // wireRowFormReactivity (formly would otherwise evaluate the expression
  // strings as its own), and the field is built without wrappers because
  // the panel template renders the label, required marker and messages
  // itself. The bootstrap checkbox is the exception — it carries its own
  // inline label, so that one keeps the (translated) label. Formly resets a
  // reused control's validators and re-derives them from the field, so the
  // email rule is declared here as the registered `email` validator.
  private buildRowField(column: TableWithInputColumn): FormlyFieldConfig {
    const kind = resolveTableWithInputColumnKind(column);
    const props = column.props ?? { label: '' };
    const field: FormlyFieldConfig = {
      key: column.key,
      type: column.type,
      className: column.className,
      wrappers: [],
      props: {
        ...props,
        label:
          kind === 'checkbox'
            ? this.translate.instant(props.label ?? '')
            : undefined,
        placeholder: props.placeholder
          ? this.translate.instant(props.placeholder)
          : props.placeholder,
        options: this.translateOptions(column, this.getColumnOptions(column)),
      },
    };
    if (kind === 'email') {
      field.validators = { validation: ['email'] };
    }
    return field;
  }

  // Option labels used to pass through the translate pipe when this panel
  // rendered its own <select>; the registered dropdown types render labels
  // verbatim, so they are translated up front. Cloned — column.props.options
  // stays untouched, it is what the table's own display resolves against.
  private translateOptions(
    column: TableWithInputColumn,
    options: TableWithInputColumnOption[]
  ): TableWithInputColumnOption[] {
    const labelKey = column.props?.bindLabel ?? 'label';
    return options.map(option => {
      const label = option[labelKey];
      return typeof label === 'string'
        ? { ...option, [labelKey]: this.translate.instant(label) }
        : option;
    });
  }

  // Drives everything about this row's form that a real FormlyFieldConfig
  // tree would normally handle on its own — this `rowForm` is a plain
  // FormBuilder FormGroup, not formly fields, so nothing else evaluates a
  // column's `expressions` or `dependentOptions` on its behalf. Re-evaluates
  // both, in that order, every time any field in the row changes:
  //   1. dependentOptions — narrows a column's dropdown choices to whatever
  //      matches another field's current value, clearing that column's own
  //      value if it's no longer among the (possibly new) valid choices.
  //   2. expressions — mirrors formly's expressionProperties ("model.<key>":
  //      "expr string"), computed against the row's values *after* any
  //      clearing above, so an expression referencing a just-cleared field
  //      sees its new state, not a stale one.
  // Both patch results into the form in one consolidated, silent
  // (emitEvent:false) write per pass.
  private wireRowFormReactivity(form: FormGroup): void {
    this.rowFormExpressionsSubscription?.unsubscribe();
    this.rowFormExpressionsSubscription = null;
    this.dependentColumnOptions = {};
    this.hiddenColumnKeys = new Set();
    this.requiredOverrides = {};

    const columnsWithRequired = this.columns.filter(
      column => !!column.expressions?.['props.required']
    );
    const columnsWithHide = this.columns.filter(
      column => !!column.expressions?.['hide']
    );
    const columnsWithExpressions = this.columns.filter(
      column => column.expressions && Object.keys(column.expressions).length
    );
    const columnsWithDependentOptions = this.columns.filter(
      column => !!column.dependentOptions
    );
    if (
      !columnsWithExpressions.length &&
      !columnsWithDependentOptions.length &&
      !columnsWithHide.length &&
      !columnsWithRequired.length
    ) {
      return;
    }

    const applyRowFormReactivity = (): void => {
      if (this.isApplyingRowExpressions) {
        return;
      }
      const rawValues = form.getRawValue();
      const changes: Record<string, unknown> = {};
      const nextDependentColumnOptions: Record<
        string,
        TableWithInputColumnOption[]
      > = {};

      columnsWithDependentOptions.forEach(column => {
        if (!column.dependentOptions) {
          return;
        }
        const { dependsOn, optionsByValue } = column.dependentOptions;
        const dependsOnValue = rawValues[dependsOn];
        const key =
          dependsOnValue === null || dependsOnValue === undefined
            ? ''
            : String(dependsOnValue);
        const options = optionsByValue[key] ?? [];
        nextDependentColumnOptions[column.key] = options;

        const currentValue = rawValues[column.key];
        const stillValid =
          currentValue === null ||
          currentValue === undefined ||
          currentValue === '' ||
          options.some(
            option =>
              getTableWithInputOptionValue(column, option) === currentValue
          );
        if (!stillValid) {
          changes[column.key] = null;
        }
      });
      this.dependentColumnOptions = nextDependentColumnOptions;
      // The rendered dropdown reads its choices from the formly field, not
      // from getColumnOptions. Reassigning props.options alone is not seen
      // by the registered dropdown types: they take new items from formly's
      // `props.options` expressionChanges event which are OnPush, so the
      // event is announced here the way formly's own expressions would.
      Object.entries(nextDependentColumnOptions).forEach(([key, options]) => {
        const fieldSet = this.rowFieldSets.find(set => set.column.key === key);
        const field = fieldSet?.fields[0];
        if (fieldSet && field?.props) {
          const translated = this.translateOptions(fieldSet.column, options);
          field.props.options = translated;
          fieldSet.options.fieldChanges?.next({
            field,
            type: 'expressionChanges',
            property: 'props.options',
            value: translated,
          });
          fieldSet.options.detectChanges?.(field);
        }
      });

      columnsWithExpressions.forEach(column => {
        Object.entries(column.expressions ?? {}).forEach(
          ([target, expression]) => {
            const targetKey = /^model\.(.+)$/.exec(target)?.[1];
            if (!targetKey || !form.get(targetKey)) {
              return;
            }
            const value = this.evaluateRowExpression(expression, {
              ...rawValues,
              ...changes,
            });
            if (form.get(targetKey)?.value !== value) {
              changes[targetKey] = value;
            }
          }
        );
      });

      // Last, against the row as it will stand after the writes above: a
      // hidden column is emptied (so nothing stale is saved or shown when it
      // reappears) and, below, disabled — a disabled control is skipped by
      // the form's validity, so a hidden `required` column can't block Save.
      const hiddenKeys = new Set<string>();
      columnsWithHide.forEach(column => {
        const isHidden = !!this.evaluateRowExpression(
          column.expressions?.['hide'] ?? '',
          { ...rawValues, ...changes }
        );
        if (!isHidden) {
          return;
        }
        hiddenKeys.add(column.key);
        const emptyValue = this.defaultValueFor(column);
        const current = form.get(column.key)?.value;
        if (JSON.stringify(current) !== JSON.stringify(emptyValue)) {
          changes[column.key] = emptyValue;
          this.rowModel[column.key] = emptyValue;
        } else {
          delete changes[column.key];
        }
      });
      this.hiddenColumnKeys = hiddenKeys;

      // Same convention as formly's own `props.required` expression, so a
      // config written for any other field works here unchanged: `field`
      // stands for the column (its props, and whether it is hidden) and
      // `model` for the row.
      const nextRequired: Record<string, boolean> = {};
      columnsWithRequired.forEach(column => {
        nextRequired[column.key] = !!this.evaluateRowExpression(
          column.expressions?.['props.required'] ?? '',
          { ...rawValues, ...changes },
          { field: { hide: hiddenKeys.has(column.key), props: column.props } }
        );
      });
      this.requiredOverrides = nextRequired;

      if (Object.keys(changes).length) {
        this.isApplyingRowExpressions = true;
        form.patchValue(changes, { emitEvent: false });
        this.isApplyingRowExpressions = false;
      }

      columnsWithHide.forEach(column => {
        const control = form.get(column.key);
        if (!control) {
          return;
        }
        if (hiddenKeys.has(column.key)) {
          if (control.enabled) {
            control.disable({ emitEvent: false });
          }
        } else if (control.disabled && !column.props?.disabled) {
          control.enable({ emitEvent: false });
        }
      });

      // The control carries the static rule from buildRowForm, and formly
      // reads `props.required` off the field it renders, so both follow the
      // evaluated state.
      columnsWithRequired.forEach(column => {
        const control = form.get(column.key);
        const field = this.rowFieldSets.find(
          set => set.column.key === column.key
        )?.fields[0];
        if (!control) {
          return;
        }
        if (field?.props) {
          field.props.required = nextRequired[column.key];
        }
        if (nextRequired[column.key]) {
          control.addValidators(Validators.required);
        } else {
          control.removeValidators(Validators.required);
        }
        control.updateValueAndValidity({ emitEvent: false });
      });
    };

    this.rowFormExpressionsSubscription = form.valueChanges.subscribe(
      applyRowFormReactivity
    );
    // Run once immediately so a pre-filled edit-mode row computes right away
    // rather than waiting for the user to touch a dependency field first.
    applyRowFormReactivity();
  }

  private evaluateRowExpression(
    expression: string,
    model: Record<string, unknown>,
    extraScope: Record<string, unknown> = {}
  ): unknown {
    try {
      return evaluateSafeExpression(expression, { model, ...extraScope });
    } catch (error) {
      console.error(
        `Error evaluating table row expression "${expression}":`,
        error
      );
      return undefined;
    }
  }

  openAddPanel(): void {
    if (this.isMaxItemsReached || this.formControl.disabled) {
      return;
    }
    this.panelMode = 'add';
    this.editingRowId = null;
    this.uniquenessErrors = [];
    this.uniqueFieldsCombinationError = null;
    this.rowForm = this.buildRowForm();
    this.panelOpen = true;
  }

  openEditPanel(row: TableWithInputRow): void {
    if (!this.enableEdit || this.formControl.disabled) {
      return;
    }
    this.panelMode = 'edit';
    this.editingRowId = row.__rowId;
    this.uniquenessErrors = [];
    this.uniqueFieldsCombinationError = null;
    this.rowForm = this.buildRowForm(row);
    this.rowForm.markAsPristine();
    this.panelOpen = true;
  }

  closePanel(): void {
    this.panelOpen = false;
    this.editingRowId = null;
    this.rowFieldSets = [];
    this.rowModel = {};
    this.uniquenessErrors = [];
    this.uniqueFieldsCombinationError = null;
    this.rowFormExpressionsSubscription?.unsubscribe();
    this.rowFormExpressionsSubscription = null;
    this.rowFormUniquenessSubscription?.unsubscribe();
    this.rowFormUniquenessSubscription = null;
  }

  cancelPanel(): void {
    if (this.rowForm?.dirty) {
      this.confirmTitle = this.translate.instant(
        this.panelMode === 'add' ? 'Discard new item' : 'Discard Changes'
      );
      this.confirmMessage = this.translate.instant(
        this.panelMode === 'add'
          ? 'You have unsaved changes. Do you want to discard them? This action cannot be reverted'
          : 'All changes made on this item will be lost. This action cannot be reverted'
      );
      this.confirmMessageValue = null;
      this.confirmAcceptLabel = this.translate.instant('Discard');
      this.confirmCancelLabel = this.translate.instant('Continue Editing');
      this.confirmVariant = 'warning';
      this.confirmAction = () => this.closePanel();
      this.modalService.open(this.confirmModalTpl, {
        centered: true,
        windowClass: 'table-with-input-confirm-modal',
      });
    } else {
      this.closePanel();
    }
  }

  // Re-run on every row-form value change (see wireRowFormUniquenessChecking)
  // as well as on Save, so a duplicate surfaces the moment it's entered
  // rather than only once the user tries to save.
  private checkUniqueness(values: Record<string, unknown>): {
    columnErrors: UniquenessError[];
    combinationError: CombinationUniquenessError | null;
  } {
    const columnErrors = this.columns
      .filter(column => column.props?.unique)
      .map(column => {
        const value = values[column.key];
        const isDuplicate =
          value !== null &&
          value !== undefined &&
          value !== '' &&
          this.rows.some(
            row =>
              row.__rowId !== this.editingRowId &&
              (row as Record<string, unknown>)[column.key] === value
          );
        return isDuplicate
          ? {
              key: column.key,
              message:
                column.validation?.messages?.unique ||
                'This value already exists',
            }
          : null;
      })
      .filter((error): error is UniquenessError => !!error);

    return {
      columnErrors,
      combinationError: this.checkUniqueFieldsCombination(values),
    };
  }

  // Composite check, separate from the per-column `unique` flag above: a row
  // only counts as a duplicate here when BOTH configured fields match another
  // row's values, not either one alone. Unlike the per-column errors above,
  // this is one condition spanning two fields, so it returns a single
  // message (naming the two duplicated values) rather than one entry per
  // field. `values` and `message` are kept apart (rather than one
  // pre-joined string) so the template can run `message` through the
  // `translate` pipe on its own -- the joined string can never match a
  // translation table entry since it always embeds the row's actual data,
  // which is exactly the resolved, already-translated-where-applicable
  // display text `formatCellValue` would show in the table itself (option
  // labels, Yes/No for checkboxes, etc.), not the raw stored value.
  private checkUniqueFieldsCombination(
    values: Record<string, unknown>
  ): CombinationUniquenessError | null {
    const config = this.uniqueFieldsCombination;
    if (!config?.enabled) {
      return null;
    }
    const [keyA, keyB] = config.fields;
    const valueA = values[keyA];
    const valueB = values[keyB];
    if (
      valueA === null ||
      valueA === undefined ||
      valueA === '' ||
      valueB === null ||
      valueB === undefined ||
      valueB === ''
    ) {
      return null;
    }

    const isDuplicate = this.rows.some(row => {
      const otherRow = row as Record<string, unknown>;
      return (
        row.__rowId !== this.editingRowId &&
        otherRow[keyA] === valueA &&
        otherRow[keyB] === valueB
      );
    });
    if (!isDuplicate) {
      return null;
    }

    // Always name the two actual duplicated values (not the field labels)
    // -- a custom `message` can be any generic wording (e.g. reused from a
    // column's own uniqueness message) and shouldn't be able to hide what
    // actually collided, so it's appended as extra context rather than
    // replacing the values outright. formatCellValue resolves the same
    // display text the table itself would show (option labels, Yes/No for
    // checkboxes, etc.), not the raw stored value.
    const pseudoRow = { ...values, __rowId: '' } as TableWithInputRow;
    const displayA = this.formatCellValue(pseudoRow, {
      key: keyA,
    } as TableColumn);
    const displayB = this.formatCellValue(pseudoRow, {
      key: keyB,
    } as TableColumn);
    return {
      values: `${displayA} and ${displayB}`,
      message: config.message || 'value already exist',
      isCustomMessage: !!config.message,
    };
  }

  // Wires uniqueness checking (both the per-column `unique` flag and
  // uniqueFieldsCombination) to the currently open row form so a duplicate
  // is flagged the moment it's entered, not only when Save is clicked.
  // Unconditional, unlike wireRowFormReactivity: every table with a `unique`
  // column or uniqueFieldsCombination configured needs this, which is far
  // more common than expressions/dependentOptions.
  private wireRowFormUniquenessChecking(form: FormGroup): void {
    const applyUniquenessCheck = (): void => {
      const { columnErrors, combinationError } = this.checkUniqueness(
        form.getRawValue()
      );
      this.uniquenessErrors = columnErrors;
      this.uniqueFieldsCombinationError = combinationError;
    };

    this.rowFormUniquenessSubscription =
      form.valueChanges.subscribe(applyUniquenessCheck);
    // Run once immediately so a pre-filled edit-mode row that's already a
    // duplicate (e.g. of a row added since) is flagged without requiring the
    // user to touch a field first.
    applyUniquenessCheck();
  }

  getUniquenessError(key: string): string | undefined {
    return this.uniquenessErrors.find(error => error.key === key)?.message;
  }

  // Only for the one-off toast below, which (unlike the panel's own
  // `| translate` pipe binding on error.message) doesn't need to stay
  // reactive to a later language change while it's showing.
  private formatCombinationError(error: CombinationUniquenessError): string {
    const separator = error.isCustomMessage ? ': ' : ' ';
    return `${error.values}${separator}${this.translate.instant(
      error.message
    )}`;
  }

  private commitRows(): void {
    // __rowId is component-only metadata for keying UI actions — strip it so
    // it never gets written into the submitted application data.

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = this.rows.map(({ __rowId, ...rest }) => rest);
    // `rows` is already up to date (we're writing it, not reading it back),
    // so the ngOnInit subscription skips syncing while isCommittingRows is
    // set — otherwise it would treat this self-write as an external patch
    // and rebuild `rows` from the stripped `values`, regenerating every
    // __rowId and desyncing anything keyed off the old ones (e.g.
    // selectedRows). setValue must still emit (see isCommittingRows above):
    // formly's own FieldType subscribes to this control's valueChanges to
    // keep the plain `model` object in sync, and consumers that submit
    // `model` directly rely on that to see this field's actual value.
    this.isCommittingRows = true;
    this.formControl.setValue(values);
    this.isCommittingRows = false;
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  save(): boolean {
    if (this.rowForm.invalid) {
      this.rowForm.markAllAsTouched();
      return false;
    }

    const values = this.rowForm.getRawValue();
    const { columnErrors, combinationError } = this.checkUniqueness(values);
    this.uniquenessErrors = columnErrors;
    this.uniqueFieldsCombinationError = combinationError;
    if (columnErrors.length || combinationError) {
      this.toastService.show({
        title: this.translate.instant('Duplicate value'),
        body: combinationError
          ? this.formatCombinationError(combinationError)
          : this.translate.instant(columnErrors[0].message),
        type: 'error',
      });
      return false;
    }

    if (this.panelMode === 'add') {
      const newRow: TableWithInputRow = {
        ...values,
        __rowId: generateRowId(),
      };
      this.rows = [...this.rows, newRow];
    } else {
      this.rows = this.rows.map(row =>
        row.__rowId === this.editingRowId ? { ...row, ...values } : row
      );
    }
    this.commitRows();
    return true;
  }

  onSave(): void {
    if (this.save()) {
      this.closePanel();
    }
  }

  deleteRow(row: TableWithInputRow): void {
    if (!this.enableDeletion || this.formControl.disabled) {
      return;
    }
    const firstColumn = this.columns[0];
    const displayValue = firstColumn
      ? (row as Record<string, unknown>)[firstColumn.key]
      : '';
    this.confirmTitle = this.translate.instant('Delete Item');
    this.confirmMessagePrefix = `${this.translate.instant(
      'Do you want to delete'
    )} "`;
    this.confirmMessageValue = String(displayValue);
    this.confirmMessageSuffix = `" ${this.translate.instant(
      'from the list. This action cannot be reverted'
    )}`;
    this.confirmAcceptLabel = this.translate.instant('Delete');
    this.confirmCancelLabel = this.translate.instant('Cancel');
    this.confirmVariant = 'danger';
    this.confirmAction = () => {
      this.rows = this.rows.filter(r => r.__rowId !== row.__rowId);
      this.selectedRows = this.selectedRows.filter(
        r => r.__rowId !== row.__rowId
      );
      this.commitRows();
    };
    this.modalService.open(this.confirmModalTpl, {
      centered: true,
      windowClass: 'table-with-input-confirm-modal',
    });
  }

  onSelectionChanged(selected: TableWithInputRow[]): void {
    this.selectedRows = this.enableRowSelection ? selected : [];
  }

  bulkDelete(): void {
    if (!this.enableRowSelection || !this.selectedRows.length) {
      return;
    }
    this.confirmTitle = this.translate.instant('Delete Item');
    this.confirmMessage = `${this.translate.instant('Do you want to delete')} ${
      this.selectedRows.length
    } ${
      this.selectedRows.length === 1
        ? this.translate.instant(
            'selected item from the list? This action cannot be reverted'
          )
        : this.translate.instant(
            'selected items from the list? This action cannot be reverted'
          )
    }`;
    this.confirmMessageValue = null;
    this.confirmAcceptLabel = this.translate.instant('Delete');
    this.confirmCancelLabel = this.translate.instant('Cancel');
    this.confirmVariant = 'danger';
    this.confirmAction = () => {
      const idsToRemove = new Set(this.selectedRows.map(row => row.__rowId));
      this.rows = this.rows.filter(row => !idsToRemove.has(row.__rowId));
      this.selectedRows = [];
      this.commitRows();
    };
    this.modalService.open(this.confirmModalTpl, {
      centered: true,
      windowClass: 'table-with-input-confirm-modal',
    });
  }

  onConfirmAccept(): void {
    this.confirmAction?.();
    this.confirmAction = null;
  }
}
